import React, { useCallback, useMemo, useState } from 'react';
import { Content, Header } from '@backstage/core-components';
import { Button, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { useSearchParams } from 'react-router-dom';
import { FlexBox } from '../../global/FlexBox';
import { DiamDataGridTabs } from '../components/DiamDataGrid/types';
import { GridTransition } from '../components/DiamDataGrid/styles';
import { RolesGrid } from '../components/RolesGrid/RolesGrid';
import { TeamRolesGrid } from '../components/TeamRolesGrid/TeamRolesGrid';
import { CreateRoleDrawer } from '../components/CreateRoleDrawer/CreateRoleDrawer';
import { ROLE_CATEGORY_QUERY_PARAMETER } from '../components/RoleDetails/constants';
import { ROLE_CATEGORIES } from '../components/RoleDetails/types';

const PREDEFINED_ROLES_CATEGORY = ROLE_CATEGORIES.predefined;
const CUSTOM_ROLES_CATEGORY = ROLE_CATEGORIES.custom;

export default function RolesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const activeRoleCategory =
    searchParams.get(ROLE_CATEGORY_QUERY_PARAMETER) === CUSTOM_ROLES_CATEGORY
      ? CUSTOM_ROLES_CATEGORY
      : PREDEFINED_ROLES_CATEGORY;

  const selectRoleCategory = useCallback(
    (selectedRoleCategory: string) => {
      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.set(
        ROLE_CATEGORY_QUERY_PARAMETER,
        selectedRoleCategory,
      );
      setSearchParams(updatedSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const tabs = useMemo<DiamDataGridTabs>(
    () => ({
      ariaLabel: 'Role type',
      items: [
        {
          label: 'Predefined Roles',
          value: PREDEFINED_ROLES_CATEGORY,
          tooltip:
            'Predefined Roles provide the basis for further Custom Roles creation for Team Admins. Predefined Roles cannot be modified or deleted',
        },
        {
          label: 'Custom Roles',
          value: CUSTOM_ROLES_CATEGORY,
          tooltip:
            'Custom Roles can be created based on Predefined Roles. Custom Roles can combine multiple Predefined Roles and already created Custom Roles',
        },
      ],
      onChange: selectRoleCategory,
      value: activeRoleCategory,
    }),
    [activeRoleCategory, selectRoleCategory],
  );

  return (
    <>
      <Header
        pageTitleOverride="Roles"
        title={
          <FlexBox sx={{ justifyContent: 'space-between', width: '100%' }}>
            <Typography variant="h4">Roles</Typography>
            {activeRoleCategory === CUSTOM_ROLES_CATEGORY ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setIsCreateOpen(true)}
              >
                ADD NEW ROLE
              </Button>
            ) : null}
          </FlexBox>
        }
      />
      <Content>
        <GridTransition key={activeRoleCategory}>
          {activeRoleCategory === PREDEFINED_ROLES_CATEGORY ? (
            <RolesGrid tabs={tabs} />
          ) : (
            <TeamRolesGrid tabs={tabs} />
          )}
        </GridTransition>
      </Content>
      <CreateRoleDrawer
        isOpen={isCreateOpen}
        handleDrawerClose={() => setIsCreateOpen(false)}
      />
    </>
  );
}
